import { Component, forwardRef, inject, Input, OnChanges, OnInit, Provider, SimpleChanges } from '@angular/core';
import {
  AbstractControl,
  FormControl,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
  UntypedFormGroup,
  ValidationErrors,
  Validators
} from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { AlertComponent, FormGroupComponent, FormsUtil, InputComponent, SelectComponent, ToastService, WizardStep } from 'mbs-ui-kit';
import { HostDataMask } from 'mbs-ui-shared@constants';
import { unidentifiedErrorText } from 'mbs-ui-shared@interceptors/error-handler.interceptor';
import { minimalWindowsVersionForNewAsync } from 'mbs-ui-shared@models/Computer';
import { first } from 'rxjs';
import { tap } from 'rxjs/operators';
import { HostType, SelectHostStepValue } from 'wizards/models/select-host-models';
import { VirtualMachinesSelectedWithNumbersType, VirtualMachinesType } from 'wizards/models/select-virtual-machines-models';
import { RemoteManagementWizardsService } from 'wizards/services/remote-management-wizards.service';
import { ParamsForCheckVMSocketLicenses, ParamsForTestCredentials, WizardStepsService } from 'wizards/services/wizard-steps.service';

import { I18NextPipe } from 'angular-i18next';

import { CommonModule } from '@angular/common';
import { RestoreAsDiskEnum } from 'wizards/models/type-data-models';
import { StepBase } from 'wizards/steps/StepBase.class';

const SelectHostStepValueAccessor: Provider = {
  provide: NG_VALUE_ACCESSOR,
  // eslint-disable-next-line @typescript-eslint/no-use-before-define
  useExisting: forwardRef(() => SelectHostStepComponent),
  multi: true
};

@UntilDestroy()
@Component({
  selector: 'mbs-select-host-step',
  templateUrl: './select-host-step.component.html',
  providers: [SelectHostStepValueAccessor],
  imports: [AlertComponent, CommonModule, FormGroupComponent, I18NextPipe, InputComponent, ReactiveFormsModule, SelectComponent]
})
export class SelectHostStepComponent extends StepBase<SelectHostStepValue> implements OnInit, OnChanges {
  mainService: RemoteManagementWizardsService;
  private stepService = inject(WizardStepsService);
  private toastService = inject(ToastService);

  @Input() restoreAs: RestoreAsDiskEnum;

  public readonly elementSelectors = {
    name: { hostSelect: 'hostSelect', hostAlert: 'hostAlert' }
  };

  public InitialValue: SelectHostStepValue;
  public valueAfterVerified: SelectHostStepValue;
  public valueAfterLoadHost: SelectHostStepValue;
  public passwordInputType = 'password';
  public isParsingError: boolean = null;
  public isValidCredentials: boolean = null;
  public isValidHost: boolean = null;
  public needValidateData = true;
  public needReloadHost = false;
  public notUpdatePassToInitial = false;
  public hostsList: string[] = [];

  get ibbProxmox(): boolean {
    return (
      this.isRestoreIbb &&
      (this.restoreAs === RestoreAsDiskEnum.RestoreToProxmox || this.restoreAs === RestoreAsDiskEnum.InstantRestoreToProxmox)
    );
  }

  get ibbOrProxmoxOrVMware(): boolean {
    return this.isRestoreVMWareOrProxmox || this.isRestoreIbb;
  }

  constructor() {
    const mainService = inject(RemoteManagementWizardsService);

    super();

    this.mainService = mainService;
  }

  ngOnInit(): void {
    this.initForm();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!this.isRestoreIbb || !this.stepForm) return;

    const previousValue = changes?.restoreAs?.previousValue;

    if (previousValue && changes.restoreAs.currentValue !== previousValue) {
      this.stepForm.reset({ server: '', login: '', password: '', remember: false, host: '' });
      this.hostsList = [];
      this.isValidCredentials = null;
      this.valueAfterVerified = null;
      this.needReloadHost = true;
      this.needValidateData = true;
    }
  }

  initForm(): void {
    this.stepForm = new UntypedFormGroup({
      server: new FormControl('', [Validators.required, this.parsingValidator.bind(this), this.hostNameIsValid.bind(this)]),
      login: new FormControl('', [Validators.required, this.parsingValidator.bind(this), this.hostCredentialsIsValid.bind(this)]),
      password: new FormControl('', [Validators.required, this.parsingValidator.bind(this), this.hostCredentialsIsValid.bind(this)]),
      remember: new FormControl(false),
      host: new FormControl('', [Validators.required])
    });

    this.initFormEvents();
  }

  onStepFormChange(value: SelectHostStepValue): void {
    if (this.valueAfterVerified) {
      this.needValidateData =
        this.valueAfterVerified.login !== value.login ||
        this.valueAfterVerified.password !== value.password ||
        this.valueAfterVerified.server !== value.server;
    }

    this.value = {
      ...value,
      valid: this.stepForm.valid && (this.isRDMode || this.isOffline || (!this.needValidateData && !this.needReloadHost))
    };

    if (
      this.stepForm.valid &&
      (this.isCreate ||
        (this.isRestoreIbb && !this.valueAfterVerified) ||
        (this.valueAfterVerified && this.valueAfterVerified?.host !== value.host))
    ) {
      this.updateHostsData(this.value);
    }
  }

  parsingValidator(control: AbstractControl): ValidationErrors | null {
    return this.isRestoreVMWareOrProxmox && this.isParsingError && HostDataMask.test(control.value) ? { isParsingError: true } : null;
  }

  hostNameIsValid(): ValidationErrors | null {
    return this.isValidHost === false ? { invalidHost: true } : null;
  }

  hostCredentialsIsValid(): ValidationErrors | null {
    return this.isValidCredentials === false ? { invalidCredentials: true } : null;
  }

  updateForm(value: SelectHostStepValue): void {
    if (this.isEdit) {
      this.InitialValue = value;
      this.valueAfterVerified = { server: value.server, login: value.login, password: value.password, remember: null };

      if (this.ibbOrProxmoxOrVMware) {
        this.valueAfterVerified.host = value.host;
      }
    }

    if (this.ibbOrProxmoxOrVMware && (this.isEdit || value.host) && value.server && value.password && value.login) {
      this.getHosts(value);
      this.updateHostsData(value);
    }

    this.toggleFormControls(['host'], this.ibbOrProxmoxOrVMware);

    this.stepForm.reset(value);
  }

  forceValid(step: WizardStep & { isSave?: boolean; isNext?: boolean } = null): void {
    if (!step.isNext && !step.isSave) return;

    const credentials = this.value?.login && this.value.password && this.value.server;

    if ((this.ibbOrProxmoxOrVMware && !this.hostsList?.length && !this.value?.host && credentials) || this.needReloadHost) {
      return void this.getHosts(this.value);
    }

    if (!this.isRDMode && !this.isOffline && this.needValidateData && this.stepForm.valid && step.isNext) {
      return void this.runValidationRequests(step.isSave);
    }

    FormsUtil.triggerValidation(this.stepForm);
    this.resetValidStateForValidFields();
  }

  updateHostsData(value: SelectHostStepValue): void {
    this.stepService.updateProperty('hosts', 'login', value.login);
    this.stepService.updateProperty('hosts', 'server', value.server);
    this.stepService.updateProperty('hosts', 'password', value.password);
    this.stepService.updateProperty('hosts', 'host', value.host);
  }

  getHosts(value: SelectHostStepValue): void {
    this.loadInfo.emit({ loading: true });

    const params = {
      agentType: 'backup',
      commandType: 'GetVirtualMachineHostList',
      params: {
        PlanId: this.mainService.planId,
        Type: this.getType(),
        Server: value.server,
        Login: value.login,
        Password: value.password,
        IsCluster: false,
        Offset: null,
        Limit: null,
        Order: 'DisplayNameAsc',
        Search: null
      }
    };
    this.hostsList = [];

    this.stepService
      .getRemoteCommandData(params, this.mainService.targetComputerData?.hid || this.mainService.hid)
      .pipe(first(), untilDestroyed(this))
      .subscribe({
        next: (result: { data: { items: { name: string }[] } }) => {
          this.hostsList = result?.data?.items?.map((h) => h.name) || [];
          this.valueAfterLoadHost = {
            server: value.server,
            login: value.login,
            password: value.password,
            remember: null
          };
          if (this.needReloadHost) {
            this.needReloadHost = false;
            this.stepForm.get('host').reset();
          }
          this.loadInfo.emit({ loading: false });
        },
        error: (e) => {
          const invalidPass = e?.error?.errorCode === 4999 || e?.error?.detail?.includes('Invalid password');
          this.hostsList = [];
          this.isParsingError = !!e?.error?.detail?.includes('-+={}[]');
          this.isValidCredentials = !invalidPass && !e?.error?.detail?.includes('The credentials for the host server');
          this.isValidHost = !e?.error?.detail?.includes(`: '${this.value.server}'`) && !e?.error?.detail?.includes(' URI: ');
          this.stepForm.get('host').reset('');
          const invalidFields = !this.isValidCredentials || !this.isValidHost || this.isParsingError;
          if (invalidFields) FormsUtil.triggerValidation(this.stepForm);
          if (!invalidFields || invalidPass) {
            this.toastService.error(e?.error?.detail || e?.error?.message || e?.message || unidentifiedErrorText);
          }
          this.loadInfo.emit({ loading: false });
        }
      });
  }

  runValidationRequests(isSave: boolean): void {
    this.loadInfo.emit({ loading: true });

    const needOldAsync = (+this.mainService?.backupVersionUpdated?.substring(0, 3) || 0) < minimalWindowsVersionForNewAsync;

    this.stepService
      .getRemoteCommandData(this.getParams(false), this.mainService.targetComputerData?.hid || this.mainService.hid, needOldAsync)
      .pipe(
        first(),
        tap((result) => {
          this.isValidCredentials = !!result?.data?.result;
          this.isValidHost = true;

          if (this.isValidCredentials) {
            this.needValidateData = false;
            this.valueAfterVerified = this.stepForm.value;
            this.notUpdatePassToInitial = this.valueAfterVerified?.password !== this.InitialValue?.password;
          }

          FormsUtil.triggerValidation(this.stepForm);

          if (this.stepForm.valid) this.nextStep.emit(isSave);
        }),
        untilDestroyed(this)
      )
      .subscribe({
        next: () => this.loadInfo.emit({ loading: false }),
        error: (e) => {
          this.isValidHost = e?.error?.errorCode !== 4999 && !e?.error?.detail?.includes('The remote name could not be resolved:');
          this.isValidCredentials = !e?.error?.detail?.includes('ESXi credential');

          FormsUtil.triggerValidation(this.stepForm);

          this.loadInfo.emit({ loading: false });
        }
      });
  }

  getParams(isVcenterCheck = true): ParamsForTestCredentials | ParamsForCheckVMSocketLicenses {
    const formValue = this.stepForm.value;

    if (isVcenterCheck) {
      return {
        agentType: 'backup',
        commandType: 'CheckVMSocketLicenses',
        params: {
          PlanId: this.mainService.planId,
          Type: this.getType(),
          Server: formValue.server,
          UserName: formValue.login,
          Password: formValue.password,
          VMBackupType: +VirtualMachinesSelectedWithNumbersType['All'],
          Machines: []
        }
      };
    } else {
      return {
        agentType: 'backup',
        commandType: 'CheckVMCredentials',
        params: {
          Type: this.isProxmox || this.isRestoreProxmox || this.ibbProxmox ? HostType.Proxmox : HostType.VMWare,
          Server: formValue.server,
          Login: formValue.login,
          Password: formValue.password
        }
      };
    }
  }

  resetIsValidCredentials(): void {
    if (this.ibbOrProxmoxOrVMware) {
      const value = this.stepForm.value;
      this.needReloadHost =
        !!this.valueAfterLoadHost &&
        (this.valueAfterLoadHost.server !== value.server ||
          this.valueAfterLoadHost.login !== value.login ||
          this.valueAfterLoadHost.password !== value.password);
      this.stepService.updateProperty('hosts', 'host', value.host);

      if (this.isParsingError) {
        this.isParsingError = false;
        this.stepForm.get('server').updateValueAndValidity();
        this.stepForm.get('login').updateValueAndValidity();
        this.stepForm.get('password').updateValueAndValidity();
      }
    }

    if (this.isValidCredentials === false || this.isValidHost === false) {
      this.isValidCredentials = null;
      this.isValidHost = null;
      this.needValidateData = true;
      this.stepForm.get('server').updateValueAndValidity();
      this.stepForm.get('login').updateValueAndValidity();
      this.stepForm.get('password').updateValueAndValidity();
    }
  }

  changeTypePassword(): void {
    this.passwordInputType = this.passwordInputType === 'password' ? 'text' : 'password';
  }

  passwordFocusHandler(event): void {
    if (event?.target?.value && this.InitialValue && event.target.value === this.InitialValue.password) {
      this.stepForm.get('password').setValue('');
    }
  }

  passwordBlurHandler(event): void {
    if (event?.target && !event.target.value && this.InitialValue?.password && !this.notUpdatePassToInitial) {
      this.stepForm.get('password').setValue(this.InitialValue.password);
    }
  }

  private getType(): VirtualMachinesType {
    if (this.isRestoreIbb) {
      return this.restoreAs === RestoreAsDiskEnum.RestoreToProxmox || this.restoreAs === RestoreAsDiskEnum.InstantRestoreToProxmox
        ? VirtualMachinesType.Proxmox
        : VirtualMachinesType.VMWare;
    }

    return this.isRestoreProxmox ? VirtualMachinesType.Proxmox : VirtualMachinesType.VMWare;
  }
}
